import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'

import { RoutingPaths } from 'app/routes/routes'

// import { MplusPayment } from 'widgets/mplus/mplus-payment/ui'
import findTicketStore from 'entities/beket/models/find-ticket/find-ticket-store'
import formPassengersState from 'entities/beket/models/form-passengers/form-passengers-state'
import beketFormPassengersStore from 'entities/beket/models/form-passengers/form-passengers-state'
import beketListTicketsStore from 'entities/beket/models/ticket/beket-list-tickets/beket-list-tickets-store'

// import userStore from 'entities/user/module/user-store'
import { BuyTicketApi } from 'shared/api/BuyTicket'
import { OrderApiBeket, ResponseReserveSeats } from 'shared/api/beket/order'
// import { CreditInfo } from 'shared/api/mplus/credit-info'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { usePrivacyPolicyDocument } from 'shared/hooks'
import ErrorImage from 'shared/images/error.png'
import { dateFormat } from 'shared/lib/dateFormat'
import { getErrorMessage } from 'shared/lib/errorText'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { preparePhoneNumberForRequest } from 'shared/lib/prepare-phone-number'
// import { getUserCommission } from 'shared/lib/getUserCommission'
import { prepareBeketRouteData } from 'shared/lib/prepareBeketRouteData'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { ListBlock } from 'shared/ui/list-block'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import { PayButton } from '../../../entities/beket/ui/pay-button'
import { DetailCard } from '../beket-trip-details/components/detail-card'

import { BeketRenderPassengerInfo } from './components/beket-render-passenger-info'
import { preparePassengerInfo } from './libs'
import { prepareDateUpdateOrder } from './libs/prepareDataUpdate'
import s from './styles.module.scss'

const CheckDataBeket = observer(() => {
  const { t } = useTranslation()
  const { openPrivacyPolicy } = usePrivacyPolicyDocument()

  // const dayMplusInfo = useRef<number>()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(false)

  const { selectedTicket } = beketListTicketsStore

  const countPassengerLength = findTicketStore.getLengthOfPassengers()

  const routeData = prepareBeketRouteData(selectedTicket!)

  const route = selectedTicket?.route

  const routeTitle = `${route?.departure_station.city} - ${route?.arrival_station.city}`

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const firstTouchOrder = async () => {
    const listPassengers = prepareDateUpdateOrder({
      data: beketFormPassengersStore.formPassengers,
      citizenship: beketFormPassengersStore.seatsResponse?.countries,
    })

    if (!listPassengers) throw Error('Error prepare passenger')

    const phoneForm = beketFormPassengersStore.formPassengers!.phone

    const { email } = beketFormPassengersStore.formPassengers!

    return OrderApiBeket.updateOrderBeket({
      contact_email: email,
      passengers: listPassengers,
      price: parseInt(selectedTicket?.price || '0'),
      contact_phone_number: preparePhoneNumberForRequest(phoneForm) || '',
      order_id: (
        beketFormPassengersStore?.seatsResponse as ResponseReserveSeats
      )?.order_id,
    })
  }

  const onRegularPay = async () => {
    if (!beketFormPassengersStore.formPassengers) return

    try {
      setLoading(true)

      const { data: OrderResponse } = await firstTouchOrder()

      confirmGetTicketBeket(OrderResponse?.data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const confirmGetTicketBeket = async (response: ResponseReserveSeats) => {
    try {
      setLoading(true)

      // TODO remove after check

      const { data } = await BuyTicketApi.buyTicketConfirm({
        id: response?.order_id,
        type: 'bus_ticket_order',
      })

      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onShowConform = () => {
    void openPrivacyPolicy('base_public_offer')
  }

  useEffect(() => {
    if (!selectedTicket) {
      navigate(RoutingPaths.beket.path())
    }
  }, [])

  const ticketDate = dateFormat(
    selectedTicket?.departure_datetime!,
    DATE_FORMAT_LIST.FULL_DATE_BEKET,
  )

  const formData = formPassengersState.getFormPassengerStore()

  const adult = formData?.adult
  const child = formData?.child

  let numberPassenger = 0
  return (
    <div>
      <HeaderGoBack
        variant="white"
        title={t('purchase_page.check_data')}
        buttonWhite
      />

      {loading ? (
        <Loader marginTop={130} />
      ) : (
        <div className={clsx('container', s.checkDataWrapper)}>
          <DetailCard
            title={routeTitle}
            subtitle={`${ticketDate} • ${t('pass_count', { count: countPassengerLength })}`}
            routeData={routeData}
          />

          {adult?.map((item) => (
            <BeketRenderPassengerInfo
              key={nanoid()}
              passenger={preparePassengerInfo(item)}
              title={`${t('passenger_each')} ${++numberPassenger}`}
            />
          ))}

          {child?.map((item) => (
            <BeketRenderPassengerInfo
              key={nanoid()}
              passenger={preparePassengerInfo(item)}
              title={`${t('passenger_each')} ${++numberPassenger}`}
              isChild
            />
          ))}

          <ListBlock
            title={t('purchase_page.contact_title')}
            list={[
              {
                label: t('email'),
                value: formData?.email || '',
              },
              {
                label: t('purchase_page.phone_number'),
                value: preparePhoneNumberForRequest(formData?.phone) || '',
              },
            ]}
          />

          <PayButton
            onSubmit={onRegularPay}
            count={countPassengerLength}
            onShowDocument={onShowConform}
            price={String(selectedTicket?.price)}
          />
        </div>
      )}
    </div>
  )
})

export default CheckDataBeket
